import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import ModuleGate from '@/components/ModuleGate';
import { 
  Users, Plus, Loader2, Calendar, AlertTriangle, CheckCircle2, 
  ChevronLeft, ChevronRight, Trash2, Edit2, User, Briefcase,
  Clock, TrendingUp, AlertCircle, Filter, Download, RefreshCw,
  Palmtree, Heart, GraduationCap, MoreHorizontal, CalendarDays,
  ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown
} from 'lucide-react';

// Import refactored components
import CapacityDialog from '@/components/resource-planning/CapacityDialog';
import AbsenceDialog from '@/components/resource-planning/AbsenceDialog';
import AllocationDialog from '@/components/resource-planning/AllocationDialog';
import UtilizationDetails from '@/components/resource-planning/UtilizationDetails';
import FilterBar from '@/components/resource-planning/FilterBar';
import KpiDashboard from '@/components/resource-planning/KpiDashboard';
import EmployeeTimelineRow from '@/components/resource-planning/EmployeeTimelineRow';

// Import custom hooks
import { useResourceCalculations } from '@/hooks/useResourceCalculations';

const ResourcePlanning = ({ user, onLogout }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [utilizationData, setUtilizationData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [absences, setAbsences] = useState([]);
  
  // Date range state - start with current year's current month
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    // Default to full year view
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`
    };
  });
  
  // Dialog states
  const [showAllocationDialog, setShowAllocationDialog] = useState(false);
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter states
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [quickFilterKW, setQuickFilterKW] = useState('');
  const [quickFilterMonth, setQuickFilterMonth] = useState('');
  const [quickFilterYear, setQuickFilterYear] = useState(new Date().getFullYear().toString());
  
  // Form states
  const [allocationForm, setAllocationForm] = useState({
    user_id: '',
    project_id: '',
    allocation_percent: 100,
    start_date: '',
    end_date: '',
    role: '',
    notes: ''
  });
  
  const [absenceForm, setAbsenceForm] = useState({
    user_id: '',
    absence_type: 'vacation',
    start_date: '',
    end_date: '',
    half_day_start: false,
    half_day_end: false,
    notes: ''
  });
  
  const [capacityForm, setCapacityForm] = useState({
    weekly_hours: 40,
    notes: ''
  });

  // Timeline scroll ref
  const timelineRef = useRef(null);
  
  // State for expanded hour lanes
  const [expandedHourLanes, setExpandedHourLanes] = useState({});
  
  // State for expanded utilization calculations
  const [expandedCalcDetails, setExpandedCalcDetails] = useState({});
  
  // State for collapsible utilization details section
  const [showUtilizationDetails, setShowUtilizationDetails] = useState(true);
  
  // State for planning settings
  const [planningSettings, setPlanningSettings] = useState({
    daily_work_hours: 8.5,
    project_work_percent: 60,
    available_project_hours: 5.1
  });

  // Use custom hook for all calculations
  const {
    timelineDays,
    months,
    filteredHoursKPI,
    calculatedEmployeeUtilization,
    filteredSummaryKPI
  } = useResourceCalculations({
    dateRange,
    timelineData,
    planningSettings,
    filterEmployee,
    filterProject
  });
  
  // Toggle calculation details for an employee
  const toggleCalcDetails = (userId) => {
    setExpandedCalcDetails(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  
  // Toggle hour lane for an employee
  const toggleHourLane = (userId) => {
    setExpandedHourLanes(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  
  // Collapse all hour lanes
  const collapseAllHourLanes = () => {
    setExpandedHourLanes({});
  };
  
  // Expand all hour lanes
  const expandAllHourLanes = () => {
    if (timelineData?.employees) {
      const allExpanded = {};
      timelineData.employees.forEach(emp => {
        allExpanded[emp.user_id] = true;
      });
      setExpandedHourLanes(allExpanded);
    }
  };
  
  // Helper: Get week dates from KW number and year
  const getWeekDates = (week, year) => {
    // ISO 8601: Week 1 contains January 4th
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday, end: sunday };
  };
  
  // Helper: Apply KW filter
  const applyKWFilter = () => {
    const kw = parseInt(quickFilterKW);
    const year = parseInt(quickFilterYear) || new Date().getFullYear();
    if (kw >= 1 && kw <= 53) {
      const { start, end } = getWeekDates(kw, year);
      // Use explicit string formatting to avoid timezone issues
      const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      setDateRange({
        start: formatDate(start),
        end: formatDate(end)
      });
    }
  };
  
  // Helper: Apply Month filter
  const applyMonthFilter = () => {
    const month = parseInt(quickFilterMonth);
    const year = parseInt(quickFilterYear) || new Date().getFullYear();
    if (month >= 0 && month <= 11) {
      // Use explicit string formatting to avoid timezone issues
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      const monthStr = String(month + 1).padStart(2, '0');
      const startStr = `${year}-${monthStr}-01`;
      const endStr = `${year}-${monthStr}-${String(lastDayOfMonth).padStart(2, '0')}`;
      setDateRange({
        start: startStr,
        end: endStr
      });
      // Reset KW when applying month filter
      setQuickFilterKW('');
    }
  };
  
  // Helper: Apply Year filter
  const applyYearFilter = () => {
    const year = parseInt(quickFilterYear);
    if (year >= 2000 && year <= 2100) {
      // Use UTC to avoid timezone issues
      const startStr = `${year}-01-01`;
      const endStr = `${year}-12-31`;
      setDateRange({
        start: startStr,
        end: endStr
      });
    }
  };
  
  // Helper: Get valid KW range for a given month and year
  const getKWRangeForMonth = (month, year) => {
    // Get first day of the month
    const firstDay = new Date(year, month, 1);
    // Get last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Calculate ISO week numbers
    const getISOWeek = (date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };
    
    const startKW = getISOWeek(firstDay);
    const endKW = getISOWeek(lastDay);
    
    return { startKW, endKW };
  };

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, projectsRes, utilizationRes, timelineRes, settingsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/resource-planning/utilization?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/resource-planning/timeline?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/resource-planning/planning-settings`)
      ]);
      
      setEmployees(usersRes.data);
      setProjects(projectsRes.data.filter(p => !p.completed && !p.deleted));
      setUtilizationData(utilizationRes.data);
      setTimelineData(timelineRes.data);
      setPlanningSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Date navigation
  const navigatePeriod = (direction) => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const months = direction === 'next' ? 1 : -1;
    
    start.setMonth(start.getMonth() + months);
    end.setMonth(end.getMonth() + months);
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };


  // Calculate bar position - returns pixel values for fixed-width container
  const getBarPosition = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const rangeStart = new Date(dateRange.start);
    const rangeEnd = new Date(dateRange.end);
    
    // Clamp to visible range
    const visibleStart = start < rangeStart ? rangeStart : start;
    const visibleEnd = end > rangeEnd ? rangeEnd : end;
    
    // If completely outside visible range, return zero width
    if (visibleStart > rangeEnd || visibleEnd < rangeStart) {
      return { left: '0px', width: '0px' };
    }
    
    const startOffset = Math.floor((visibleStart - rangeStart) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((visibleEnd - visibleStart) / (1000 * 60 * 60 * 24)) + 1;
    
    return {
      left: `${startOffset * DAY_WIDTH}px`,
      width: `${duration * DAY_WIDTH}px`
    };
  };

  // Handle allocation submit
  const handleAllocationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (editingAllocation) {
        await axios.put(`${API}/resource-planning/allocations/${editingAllocation.id}`, allocationForm);
        toast.success('Allokation aktualisiert');
      } else {
        await axios.post(`${API}/resource-planning/allocations`, allocationForm);
        toast.success('Allokation erstellt');
      }
      
      setShowAllocationDialog(false);
      setEditingAllocation(null);
      resetAllocationForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle absence submit
  const handleAbsenceSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (editingAbsence) {
        await axios.put(`${API}/resource-planning/absences/${editingAbsence.id}`, absenceForm);
        toast.success('Abwesenheit aktualisiert');
      } else {
        await axios.post(`${API}/resource-planning/absences`, absenceForm);
        toast.success('Abwesenheit erstellt');
      }
      
      setShowAbsenceDialog(false);
      setEditingAbsence(null);
      resetAbsenceForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle capacity submit
  const handleCapacitySubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/resource-planning/capacities/${selectedEmployee.user_id}`, {
        weekly_hours: capacityForm.weekly_hours,
        effective_date: new Date().toISOString().split('T')[0],
        notes: capacityForm.notes
      });
      toast.success('Kapazität aktualisiert');
      setShowCapacityDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete handlers
  const handleDeleteAllocation = async (id) => {
    if (!confirm('Allokation wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/resource-planning/allocations/${id}`);
      toast.success('Allokation gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteAbsence = async (id) => {
    if (!confirm('Abwesenheit wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/resource-planning/absences/${id}`);
      toast.success('Abwesenheit gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Handle task adjustment for absences
  const handleAdjustTaskForAbsence = async (taskId) => {
    try {
      const response = await axios.post(`${API}/resource-planning/adjust-task-for-absences`, {
        task_id: taskId,
        apply: true
      });
      
      if (response.data.adjusted && response.data.applied) {
        toast.success(
          `Task angepasst: Neues Enddatum ${response.data.new_end_date} (+${response.data.absence_days} Arbeitstage wegen Abwesenheit)`
        );
        // Refresh timeline data
        fetchData();
      } else if (response.data.adjusted && !response.data.applied) {
        toast.error('Task konnte nicht angepasst werden');
      } else {
        toast.info('Keine Anpassung erforderlich');
      }
    } catch (error) {
      toast.error('Fehler beim Anpassen des Tasks');
      console.error(error);
    }
  };

  // Handle adjusting all tasks for a user
  const handleAdjustAllUserTasks = async (userId, userName) => {
    try {
      const response = await axios.post(`${API}/resource-planning/adjust-user-tasks/${userId}`);
      
      if (response.data.total_adjustments > 0) {
        toast.success(
          `${response.data.total_adjustments} Task(s) für ${userName} angepasst`
        );
        fetchData();
      } else {
        toast.info(`Keine Tasks von ${userName} müssen angepasst werden`);
      }
    } catch (error) {
      toast.error('Fehler beim Anpassen der Tasks');
      console.error(error);
    }
  };

  // Reset forms
  const resetAllocationForm = () => {
    setAllocationForm({
      user_id: '',
      project_id: '',
      allocation_percent: 100,
      start_date: '',
      end_date: '',
      role: '',
      notes: ''
    });
  };

  const resetAbsenceForm = () => {
    setAbsenceForm({
      user_id: '',
      absence_type: 'vacation',
      start_date: '',
      end_date: '',
      half_day_start: false,
      half_day_end: false,
      notes: ''
    });
  };

  // Open dialogs with pre-filled data
  const openAllocationDialog = (employee = null, allocation = null) => {
    if (allocation) {
      setEditingAllocation(allocation);
      setAllocationForm({
        user_id: allocation.user_id,
        project_id: allocation.project_id,
        allocation_percent: allocation.allocation_percent,
        start_date: allocation.start_date,
        end_date: allocation.end_date,
        role: allocation.role || '',
        notes: allocation.notes || ''
      });
    } else {
      setEditingAllocation(null);
      resetAllocationForm();
      if (employee) {
        setAllocationForm(prev => ({ ...prev, user_id: employee.user_id }));
      }
    }
    setShowAllocationDialog(true);
  };

  const openAbsenceDialog = (employee = null, absence = null) => {
    if (absence) {
      setEditingAbsence(absence);
      setAbsenceForm({
        user_id: absence.user_id,
        absence_type: absence.absence_type,
        start_date: absence.start_date,
        end_date: absence.end_date,
        half_day_start: absence.half_day_start || false,
        half_day_end: absence.half_day_end || false,
        notes: absence.notes || ''
      });
    } else {
      setEditingAbsence(null);
      resetAbsenceForm();
      if (employee) {
        setAbsenceForm(prev => ({ ...prev, user_id: employee.user_id }));
      }
    }
    setShowAbsenceDialog(true);
  };

  const openCapacityDialog = (employee) => {
    setSelectedEmployee(employee);
    setCapacityForm({
      weekly_hours: employee.weekly_hours || 40,
      notes: ''
    });
    setShowCapacityDialog(true);
  };

  // Utilization status badge
  const getStatusBadge = (status, label) => {
    const variants = {
      overbooked: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
      high: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      normal: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
      low: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
    };
    
    return (
      <Badge variant="outline" className={variants[status]}>
        {status === 'overbooked' && <AlertTriangle className="w-3 h-3 mr-1" />}
        {label}
      </Badge>
    );
  };

  // Absence type icon
  const getAbsenceIcon = (type) => {
    switch (type) {
      case 'vacation': return <Palmtree className="w-3 h-3" />;
      case 'sick': return <Heart className="w-3 h-3" />;
      case 'training': return <GraduationCap className="w-3 h-3" />;
      case 'holiday': return <CalendarDays className="w-3 h-3" />;
      default: return <Calendar className="w-3 h-3" />;
    }
  };

  const getAbsenceLabel = (type) => {
    const labels = {
      vacation: 'Urlaub',
      sick: 'Krankheit',
      training: 'Weiterbildung',
      holiday: 'Feiertag',
      other: 'Sonstige'
    };
    return labels[type] || type;
  };

  const DAY_WIDTH = 28; // pixels per day

  return (
    <Layout user={user} onLogout={onLogout}>
      <ModuleGate moduleId="resource_planning">
        <div className="space-y-6" data-testid="resource-planning-page">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                <Users className="w-8 h-8 text-primary" />
                Ressourcenplanung
              </h1>
              <p className="text-muted-foreground mt-1">
                Kapazitäten planen, Auslastung verfolgen, Überbuchungen vermeiden
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => openAbsenceDialog()}>
                <Palmtree className="w-4 h-4 mr-2" />
                Abwesenheit
              </Button>
            </div>
          </div>

          {/* KPI Dashboard - Summary Cards & Hours Breakdown */}
          <KpiDashboard
            utilizationData={utilizationData}
            filteredSummaryKPI={filteredSummaryKPI}
            filteredHoursKPI={filteredHoursKPI}
            planningSettings={planningSettings}
            filterEmployee={filterEmployee}
            filterProject={filterProject}
          />

          {/* Time & Data Filters */}
          <FilterBar
            quickFilterYear={quickFilterYear}
            quickFilterMonth={quickFilterMonth}
            quickFilterKW={quickFilterKW}
            setQuickFilterYear={setQuickFilterYear}
            setQuickFilterMonth={setQuickFilterMonth}
            setQuickFilterKW={setQuickFilterKW}
            dateRange={dateRange}
            setDateRange={setDateRange}
            applyYearFilter={applyYearFilter}
            applyKWFilter={applyKWFilter}
            getWeekDates={getWeekDates}
            getKWRangeForMonth={getKWRangeForMonth}
            filterEmployee={filterEmployee}
            filterProject={filterProject}
            setFilterEmployee={setFilterEmployee}
            setFilterProject={setFilterProject}
            employees={employees}
            projects={projects}
          />

          {/* Timeline View */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Ressourcen-Timeline</CardTitle>
                  <CardDescription>
                    Mitarbeiter-Ansicht mit Projekt-Allokationen und Abwesenheiten
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={Object.keys(expandedHourLanes).some(k => expandedHourLanes[k]) ? collapseAllHourLanes : expandAllHourLanes}
                  >
                    {Object.keys(expandedHourLanes).some(k => expandedHourLanes[k]) ? (
                      <>
                        <ChevronsDownUp className="w-4 h-4 mr-1" />
                        Stunden zuklappen
                      </>
                    ) : (
                      <>
                        <ChevronsUpDown className="w-4 h-4 mr-1" />
                        Stunden aufklappen
                      </>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigatePeriod('prev')}>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Früher
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigatePeriod('next')}>
                    Später
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : timelineData?.employees?.length > 0 ? (
                <div className="overflow-hidden">
                  <ScrollArea className="w-full" ref={timelineRef}>
                    <div style={{ width: `${250 + timelineDays.length * DAY_WIDTH}px` }}>
                      {/* Month Header */}
                      <div className="flex border-b border-slate-300 dark:border-slate-700 bg-muted/30 sticky top-0 z-10">
                        <div className="w-[250px] shrink-0 p-2 font-medium text-sm border-r border-slate-300 dark:border-slate-700">
                          Mitarbeiter
                        </div>
                        <div className="flex" style={{ width: `${timelineDays.length * DAY_WIDTH}px` }}>
                          {months.map((month, idx) => (
                            <div 
                              key={idx} 
                              className="text-center text-xs font-medium py-1 border-r border-slate-300 dark:border-slate-700"
                              style={{ width: `${month.days * DAY_WIDTH}px` }}
                            >
                              {month.name}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Day Header */}
                      <div className="flex border-b border-slate-300 dark:border-slate-700 bg-muted/20 sticky top-[33px] z-10">
                        <div className="w-[250px] shrink-0 p-2 text-xs text-muted-foreground border-r border-slate-300 dark:border-slate-700">
                          Auslastung
                        </div>
                        <div className="flex" style={{ width: `${timelineDays.length * DAY_WIDTH}px` }}>
                          {timelineDays.map((day, idx) => (
                            <div 
                              key={idx}
                              className={`text-center text-[10px] py-1 border-r border-slate-300 dark:border-slate-700 ${
                                day.isWeekend ? 'bg-slate-200/70 dark:bg-slate-700/50' : ''
                              } ${day.isToday ? 'bg-blue-100 dark:bg-blue-900/40 font-bold' : ''}`}
                              style={{ width: `${DAY_WIDTH}px` }}
                            >
                              {day.dayOfMonth}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Employee Rows Container - with shared vertical grid lines */}
                      <div className="relative">
                        {/* Single background layer with vertical day lines - spans all rows */}
                        <div 
                          className="absolute top-0 left-[250px] bottom-0 pointer-events-none z-0 timeline-grid-lines"
                          style={{
                            width: `${timelineDays.length * DAY_WIDTH}px`,
                            '--day-width': `${DAY_WIDTH}px`,
                            backgroundSize: `${DAY_WIDTH}px 100%`
                          }}
                        />
                        
                        {/* Weekend highlighting layer */}
                        <div className="absolute top-0 left-[250px] bottom-0 pointer-events-none z-0 flex" style={{ width: `${timelineDays.length * DAY_WIDTH}px` }}>
                          {timelineDays.map((day, idx) => (
                            <div 
                              key={idx}
                              className={`${day.isWeekend ? 'bg-slate-200/70 dark:bg-slate-700/50' : ''} ${day.isToday ? 'bg-blue-100/30 dark:bg-blue-900/30' : ''}`}
                              style={{ width: `${DAY_WIDTH}px`, height: '100%' }}
                            />
                          ))}
                        </div>
                        
                        {/* Employee Rows */}
                        {timelineData.employees
                          .filter(employee => {
                            if (filterEmployee && employee.user_id !== filterEmployee) return false;
                            if (filterProject) {
                              const hasProject = employee.project_lanes?.some(lane => lane.project_id === filterProject);
                              if (!hasProject) return false;
                            }
                            return true;
                          })
                          .map((employee) => {
                            const utilization = calculatedEmployeeUtilization.find(e => e.user_id === employee.user_id);
                            const projectLanes = filterProject 
                              ? (employee.project_lanes || []).filter(lane => lane.project_id === filterProject)
                              : (employee.project_lanes || []);
                            
                            return (
                              <EmployeeTimelineRow
                                key={employee.user_id}
                                employee={employee}
                                utilization={utilization}
                                projectLanes={projectLanes}
                                timelineDays={timelineDays}
                                dayWidth={DAY_WIDTH}
                                expandedHourLanes={expandedHourLanes}
                                planningSettings={planningSettings}
                                getBarPosition={getBarPosition}
                                toggleHourLane={toggleHourLane}
                                openCapacityDialog={openCapacityDialog}
                                openAbsenceDialog={openAbsenceDialog}
                                handleAdjustAllUserTasks={handleAdjustAllUserTasks}
                                handleAdjustTaskForAbsence={handleAdjustTaskForAbsence}
                              />
                            );
                          })}
                      </div>
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-20 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Keine Mitarbeiter gefunden</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Utilization Details - Refactored Component */}
          {calculatedEmployeeUtilization.length > 0 && (
            <UtilizationDetails
              employees={calculatedEmployeeUtilization}
              showDetails={showUtilizationDetails}
              setShowDetails={setShowUtilizationDetails}
              expandedCalcDetails={expandedCalcDetails}
              toggleCalcDetails={toggleCalcDetails}
              filterEmployee={filterEmployee}
              filterProject={filterProject}
              planningSettings={planningSettings}
              getStatusBadge={getStatusBadge}
            />
          )}
        </div>

        {/* Allocation Dialog - Refactored Component */}
        <AllocationDialog
          open={showAllocationDialog}
          onOpenChange={setShowAllocationDialog}
          form={allocationForm}
          setForm={setAllocationForm}
          employees={employees}
          projects={projects}
          isSubmitting={isSubmitting}
          isEditing={!!editingAllocation}
          editingAllocation={editingAllocation}
          onSubmit={handleAllocationSubmit}
          onDelete={handleDeleteAllocation}
        />

        {/* Absence Dialog - Refactored Component */}
        <AbsenceDialog
          open={showAbsenceDialog}
          onOpenChange={setShowAbsenceDialog}
          form={absenceForm}
          setForm={setAbsenceForm}
          employees={employees}
          isSubmitting={isSubmitting}
          isEditing={!!editingAbsence}
          editingAbsence={editingAbsence}
          onSubmit={handleAbsenceSubmit}
          onDelete={handleDeleteAbsence}
        />

        {/* Capacity Dialog - Refactored Component */}
        <CapacityDialog
          open={showCapacityDialog}
          onOpenChange={setShowCapacityDialog}
          form={capacityForm}
          setForm={setCapacityForm}
          selectedEmployee={selectedEmployee}
          isSubmitting={isSubmitting}
          onSubmit={handleCapacitySubmit}
        />
      </ModuleGate>
    </Layout>
  );
};

export default ResourcePlanning;
